/**
 * Google Play Billing Handler - STUB VERSION
 *
 * Temporarily disabled due to expo-in-app-purchases incompatibility
 * This stub allows the app to build while we work on subscription configuration
 *
 * TODO: Re-enable with compatible IAP package after subscription products are configured
 */

import { Platform } from 'react-native';

import { GOOGLE_PLAY_PRODUCTS } from '../config/platformProducts';

type GooglePurchasePayload = {
  purchaseToken?: string;
  productId: string;
  purchaseTime?: number;
  acknowledged?: boolean;
  orderId?: string;
};

export interface GooglePlayPurchase {
  purchaseToken: string;
  productId: string;
  packageName: string;
  purchaseTime: number;
  purchaseState: 'pending' | 'purchased' | 'canceled';
  orderId: string;
  acknowledged: boolean;
}

export interface GooglePlaySubscriptionStatus {
  kind: string;
  startTimeMillis: string;
  expiryTimeMillis: string;
  autoRenewing: boolean;
  priceCurrencyCode: string;
  priceAmountMicros: string;
  cancelReason?: number;
  userCancellationTimeMillis?: string;
  paymentState?: number;
}

class GooglePlayBillingHandler {
  /**
   * Check if running on Android platform
   */
  isAndroid(): boolean {
    return Platform.OS === 'android';
  }

  /**
   * Get Google Play product IDs
   */
  getProductIds(): string[] {
    return GOOGLE_PLAY_PRODUCTS.map(product => product.id);
  }

  /**
   * Process Google Play purchase
   * Extracts Android-specific purchase data
   */
  processGooglePlayPurchase(purchase: GooglePurchasePayload): GooglePlayPurchase | null {
    if (!this.isAndroid()) {
      console.warn('processGooglePlayPurchase called on non-Android platform');
      return null;
    }

    try {
      return {
        purchaseToken: purchase.purchaseToken || '',
        productId: purchase.productId,
        packageName: 'kingdom.bible.quiz', // From app.json
        purchaseTime: purchase.purchaseTime || Date.now(),
        purchaseState: purchase.acknowledged ? 'purchased' : 'pending',
        orderId: purchase.orderId || '',
        acknowledged: purchase.acknowledged ?? false
      };
    } catch (error) {
      console.error('Error processing Google Play purchase:', error);
      return null;
    }
  }

  /**
   * Prepare purchase data for backend verification
   * Returns data needed for Google Play Developer API verification
   */
  preparePurchaseForVerification(purchase: GooglePlayPurchase): {
    packageName: string;
    subscriptionId: string;
    purchaseToken: string;
  } {
    return {
      packageName: purchase.packageName,
      subscriptionId: purchase.productId,
      purchaseToken: purchase.purchaseToken
    };
  }

  /**
   * Handle Google Play subscription notification types
   * Maps notification types to subscription states
   *
   * Notification Types:
   * 1 = SUBSCRIPTION_RECOVERED
   * 2 = SUBSCRIPTION_RENEWED
   * 3 = SUBSCRIPTION_CANCELED
   * 4 = SUBSCRIPTION_PURCHASED
   * 5 = SUBSCRIPTION_ON_HOLD
   * 6 = SUBSCRIPTION_IN_GRACE_PERIOD
   * 7 = SUBSCRIPTION_RESTARTED
   * 8 = SUBSCRIPTION_PRICE_CHANGE_CONFIRMED
   * 9 = SUBSCRIPTION_DEFERRED
   * 10 = SUBSCRIPTION_PAUSED
   * 11 = SUBSCRIPTION_PAUSE_SCHEDULE_CHANGED
   * 12 = SUBSCRIPTION_REVOKED
   * 13 = SUBSCRIPTION_EXPIRED
   */
  parseNotificationType(notificationType: number): {
    type: string;
    shouldGrantAccess: boolean;
    shouldRevokeAccess: boolean;
  } {
    const typeMap: Record<number, { type: string; shouldGrantAccess: boolean; shouldRevokeAccess: boolean }> = {
      1: { type: 'RECOVERED', shouldGrantAccess: true, shouldRevokeAccess: false },
      2: { type: 'RENEWED', shouldGrantAccess: true, shouldRevokeAccess: false },
      3: { type: 'CANCELED', shouldGrantAccess: false, shouldRevokeAccess: true },
      4: { type: 'PURCHASED', shouldGrantAccess: true, shouldRevokeAccess: false },
      5: { type: 'ON_HOLD', shouldGrantAccess: false, shouldRevokeAccess: false },
      6: { type: 'IN_GRACE_PERIOD', shouldGrantAccess: true, shouldRevokeAccess: false },
      7: { type: 'RESTARTED', shouldGrantAccess: true, shouldRevokeAccess: false },
      8: { type: 'PRICE_CHANGE_CONFIRMED', shouldGrantAccess: true, shouldRevokeAccess: false },
      9: { type: 'DEFERRED', shouldGrantAccess: false, shouldRevokeAccess: false },
      10: { type: 'PAUSED', shouldGrantAccess: false, shouldRevokeAccess: true },
      11: { type: 'PAUSE_SCHEDULE_CHANGED', shouldGrantAccess: true, shouldRevokeAccess: false },
      12: { type: 'REVOKED', shouldGrantAccess: false, shouldRevokeAccess: true },
      13: { type: 'EXPIRED', shouldGrantAccess: false, shouldRevokeAccess: true }
    };

    return typeMap[notificationType] || {
      type: 'UNKNOWN',
      shouldGrantAccess: false,
      shouldRevokeAccess: false
    };
  }

  /**
   * Parse Google Play payment state
   * 0 = Payment pending
   * 1 = Payment received
   * 2 = Free trial
   * 3 = Pending deferred upgrade/downgrade
   */
  parsePaymentState(paymentState?: number): string {
    const stateMap: Record<number, string> = {
      0: 'PAYMENT_PENDING',
      1: 'PAYMENT_RECEIVED',
      2: 'FREE_TRIAL',
      3: 'PENDING_DEFERRED'
    };

    return stateMap[paymentState || 1] || 'UNKNOWN';
  }

  /**
   * Parse Google Play cancel reason
   * 0 = User canceled
   * 1 = System canceled
   * 2 = Subscription replaced with new subscription
   * 3 = Developer canceled
   */
  parseCancelReason(cancelReason?: number): string {
    const reasonMap: Record<number, string> = {
      0: 'USER_CANCELED',
      1: 'SYSTEM_CANCELED',
      2: 'REPLACED',
      3: 'DEVELOPER_CANCELED'
    };

    return reasonMap[cancelReason || 0] || 'UNKNOWN';
  }

  /**
   * Determine if subscription is active based on status
   */
  isSubscriptionActive(status: GooglePlaySubscriptionStatus): boolean {
    const now = Date.now();
    const expiryTime = parseInt(status.expiryTimeMillis);

    // Check if subscription hasn't expired
    if (now >= expiryTime) {
      return false;
    }

    // Check if payment is received or in grace period
    const paymentState = status.paymentState;
    if (paymentState !== undefined && paymentState !== 1 && paymentState !== 2) {
      // Payment pending or deferred - not active
      return false;
    }

    return true;
  }

  /**
   * Get subscription grace period status
   * Grace period allows continued access while payment issues resolve
   */
  isInGracePeriod(status: GooglePlaySubscriptionStatus): boolean {
    // If payment state is pending but subscription hasn't expired
    return (
      status.paymentState === 0 &&
      parseInt(status.expiryTimeMillis) > Date.now()
    );
  }

  /**
   * Get subscription on-hold status
   * On-hold means payment failed and grace period expired
   */
  isOnHold(status: GooglePlaySubscriptionStatus): boolean {
    // Payment pending and past expiry = on hold
    return (
      status.paymentState === 0 &&
      parseInt(status.expiryTimeMillis) <= Date.now()
    );
  }

  /**
   * Format purchase data for database storage
   */
  formatForDatabase(purchase: GooglePlayPurchase, userId: string) {
    const planMapping = this.getProductPlanMapping(purchase.productId);

    return {
      user_id: userId,
      plan_id: planMapping?.planId || '',
      billing_interval: planMapping?.billingInterval || 'monthly',
      payment_provider: 'google' as const,
      payment_provider_id: purchase.orderId,
      platform: 'android' as const,
      purchase_token: purchase.purchaseToken,
      environment: __DEV__ ? 'sandbox' : 'production',
      status: purchase.purchaseState === 'purchased' ? 'active' : 'pending'
    };
  }

  /**
   * Get plan mapping for Google Play product ID
   */
  private getProductPlanMapping(productId: string): { planId: string; billingInterval: 'monthly' | 'annual' } | null {
    const product = GOOGLE_PLAY_PRODUCTS.find(p => p.id === productId);
    if (!product) {
      return null;
    }

    return {
      planId: product.planId,
      billingInterval: product.billingInterval
    };
  }

  /**
   * Get Google Play Console subscription management URL
   */
  getSubscriptionManagementUrl(): string {
    return 'https://play.google.com/store/account/subscriptions';
  }

  /**
   * Generate deep link to specific subscription in Google Play
   */
  getSubscriptionDeepLink(productId: string): string {
    return `https://play.google.com/store/account/subscriptions?sku=${productId}&package=kingdom.bible.quiz`;
  }

  /**
   * Validate purchase token format
   */
  isValidPurchaseToken(token: string): boolean {
    // Google Play purchase tokens are typically long alphanumeric strings
    // Basic validation: not empty, reasonable length, alphanumeric with dots/underscores
    return (
      token.length > 10 &&
      token.length < 500 &&
      /^[a-zA-Z0-9._-]+$/.test(token)
    );
  }

  /**
   * Get product display name from product ID
   */
  getProductDisplayName(productId: string): string {
    const product = GOOGLE_PLAY_PRODUCTS.find(p => p.id === productId);
    return product?.displayName || productId;
  }

  /**
   * Get all Google Play products for a specific plan
   */
  getProductsByPlanId(planId: string): typeof GOOGLE_PLAY_PRODUCTS {
    return GOOGLE_PLAY_PRODUCTS.filter(p => p.planId === planId);
  }
}

// Export singleton instance
export const googlePlayBillingHandler = new GooglePlayBillingHandler();
